import { getAccessToken } from '#lib/authToken';
import { fetcher } from '#lib/backendFetcher';

const BACKEND = process.env.REACT_APP_BACKEND_WORKER_URL;

/**
 * Fetches all email templates for the organization.
 * @returns {Promise<Object>}
 */
export const listEmailTemplates = () => fetcher('/api/v1/email/templates');

/**
 * Creates a new email template.
 * @param {Object} payload - Template data (name, subject, content, etc.)
 * @returns {Promise<Object>}
 */
export const createEmailTemplate = (payload) => fetcher('/api/v1/email/templates', { method: 'POST', body: payload });

/**
 * Updates an existing email template.
 * @param {string} id - UUID of the template
 * @param {Object} payload - Fields to update
 * @returns {Promise<Object>}
 */
export const updateEmailTemplate = (id, payload) =>
	fetcher(`/api/v1/email/templates/${id}`, { method: 'PATCH', body: payload });

/**
 * Deletes an email template.
 * @param {string} id - UUID of the template
 * @returns {Promise<Object>}
 */
export const deleteEmailTemplate = (id) => fetcher(`/api/v1/email/templates/${id}`, { method: 'DELETE' });

/**
 * Uploads (or replaces) a template's file attachment.
 *
 * Bypasses the shared `fetcher`, which always sends JSON — the attachment needs a
 * multipart/form-data body. The JWT is injected manually; Content-Type is intentionally
 * left unset so the browser adds the multipart boundary. On failure, throws an Error
 * carrying the backend `code`/`status` so callers can translate it like any other error.
 *
 * @param {string} templateId - UUID of the template
 * @param {File|Blob} file - The attachment (PDF, JPG, PNG, WebP, DOCX, XLSX or PPTX, ≤ 2 MB).
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional abort signal to cancel the upload.
 * @returns {Promise<{ attachment: { id: string, originalFilename: string, sizeBytes: number, contentType: string } }>}
 * @throws {Error} With `status` and `code` set on a non-successful response.
 */
export async function uploadEmailTemplateAttachment(templateId, file, { signal } = {}) {
	const token = await getAccessToken();

	const form = new FormData();
	form.append('file', file);

	const res = await fetch(`${BACKEND}/api/v1/email/templates/${templateId}/attachment`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: form,
		signal,
	});

	if (!res.ok) {
		let errorData = {};
		try {
			errorData = await res.json();
		} catch {
			errorData = { errorMessage: `Upload failed: ${res.status}` };
		}
		const error = new Error(errorData.errorMessage || `Upload failed: ${res.status}`);
		error.status = res.status;
		error.code = errorData.errorCode ?? null;
		error.validationErrors = errorData.validationErrors ?? null;
		throw error;
	}

	return res.json();
}

/**
 * Removes a template's file attachment.
 * @param {string} templateId - UUID of the template
 * @returns {Promise<Object>}
 */
export const deleteEmailTemplateAttachment = (templateId) =>
	fetcher(`/api/v1/email/templates/${templateId}/attachment`, { method: 'DELETE' });

/**
 * Renders unsaved template text for one CRM recipient.
 * @param {{emailSubject: string, emailContent: string, crmContactId?: string, crmCompanyId?: string}} body
 * @returns {Promise<{emailSubject: string, emailContent: string, emailRecipient: string|null, missingVariables: Array<{key: string, name: string}>}>}
 */
export const previewEmailTemplate = (body) => fetcher('/api/v1/email/templates/preview', { method: 'POST', body });
